import mapboxgl from "mapbox-gl";
import { useEffect, useRef, useState } from "react";
import { loadImages } from "./Helpers/images";
import { hasValidLatitudeLongitude } from "./Helpers/hasValidLatitudeLongitude";
import type { MapLane } from "./Helpers/setupLanes";
import { drawOneLane, frameLanes } from "./Helpers/setupLanes";
import { MAPBOX_ACCESS_TOKEN } from "@/Shared/Maps/mapboxAccessToken";
import { mapboxProjection } from "@freightsimple/ui-core/Display/mapboxProjection";
import { mapboxStyleUrl } from "@freightsimple/ui-core/Display/mapboxStyle";
import { useThemeContext } from "@freightsimple/ui-core/Theme/useThemeContext";
import { MapsFullBleedMapCanvas } from "./CustomComponents/MapsFullBleedMapCanvas";
import { useMapsResizeWithContainer } from "./Hooks/useMapsResizeWithContainer";

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

interface ShipmentsMapProps {
  lanes: MapLane[];
  /** Index of the lane to draw, or undefined to draw none. */
  highlightedLaneIndex?: number | undefined;
}

/**
 * The multi-lane sibling of ShipmentMap. The camera frames every shipment's
 * lane, but none is drawn until one is highlighted — several arcs at once read
 * as clutter rather than information, so the list draws the lane belonging to
 * whichever card the cursor is on.
 *
 * No animated marker. ShipmentMap runs one requestAnimationFrame loop to walk a
 * truck along its arc; that only makes sense for a page about one shipment.
 */
export function ShipmentsMap(props: ShipmentsMapProps) {
  const { lanes, highlightedLaneIndex } = props;

  const [map, setMap] = useState<mapboxgl.Map | undefined>();
  const mapContainer = useRef<HTMLDivElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  useMapsResizeWithContainer(map, mapContainer);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const unmountedRef = useRef(false);
  // Which lane's layers are on the map. They live on the map, not in React, so
  // the next highlight needs the previous index to clear it.
  const drawnLaneIndexRef = useRef<number | undefined>(undefined);

  const { resolvedTheme } = useThemeContext();
  const isDark = resolvedTheme === "dark";

  // Mapbox sometimes reports the style as not loaded with no event to wait on,
  // so catch and retry — same workaround as ShipmentMap.
  function withRetry(f: () => void) {
    let remaining = 3;

    function retry() {
      if (unmountedRef.current) {
        return;
      }
      try {
        f();
      } catch {
        remaining--;
        if (unmountedRef.current) {
          return;
        }
        if (remaining > 0) {
          retryTimerRef.current = setTimeout(retry, 500);
        } else {
          console.error("Ran out of retries");
        }
      }
    }

    retry();
  }

  function frameLanesWrapper(mapToFrame: mapboxgl.Map) {
    withRetry(function () {
      const lane =
        highlightedLaneIndex === undefined
          ? undefined
          : lanes[highlightedLaneIndex];

      frameLanes(mapToFrame, lane === undefined ? lanes : [lane]);
    });
  }

  function redrawHighlight(mapToDraw: mapboxgl.Map) {
    withRetry(function () {
      const lane =
        highlightedLaneIndex === undefined
          ? undefined
          : lanes[highlightedLaneIndex];

      drawOneLane(
        mapToDraw,
        lane,
        drawnLaneIndexRef.current,
        lane === undefined ? undefined : highlightedLaneIndex,
      );

      drawnLaneIndexRef.current =
        lane === undefined ? undefined : highlightedLaneIndex;
    });
  }

  useEffect(() => {
    // A Mapbox map owns a WebGL context and keeps fetching tiles for as long as
    // it lives, and it does not stop when React drops the element it drew into.
    // `created` rather than the `map` state so teardown works when unmount beats
    // the async setup below.
    let created: mapboxgl.Map | undefined;
    let cancelled = false;
    let setupFinished: Promise<void> | undefined;

    async function initializeMap() {
      const currentMapContainer = mapContainer.current;
      if (!currentMapContainer) {
        return;
      }

      try {
        const mapToInitialize = new mapboxgl.Map({
          container: currentMapContainer,
          style: mapboxStyleUrl(isDark),
          center: [-90, 44],
          zoom: 3,
          projection: mapboxProjection(),
        });
        created = mapToInitialize;

        mapToInitialize.boxZoom.disable();
        mapToInitialize.scrollZoom.disable();
        mapToInitialize.dragRotate.disable();
        mapToInitialize.dragPan.disable();
        mapToInitialize.keyboard.disable();
        mapToInitialize.doubleClickZoom.disable();
        mapToInitialize.touchZoomRotate.disable();

        mapToInitialize.resize();

        await loadImages(mapToInitialize);

        if (cancelled) {
          return;
        }

        setMapLoaded(true);
        setMap(mapToInitialize);
      } catch (e) {
        console.warn(`Exception initializing map`, { e });
      }
    }

    if (map === undefined) {
      setupFinished = initializeMap();
    }

    return function () {
      cancelled = true;
      // Wait for setup to settle before removing: remove() kills Mapbox's
      // workers, and an image load still in flight then posts to a worker that
      // is gone — an unhandled rejection.
      void Promise.resolve(setupFinished).then(function () {
        created?.remove();
      });
    };
  }, []);

  useEffect(() => {
    return function () {
      // Flag first, so clearing the timer can't be beaten by a retry firing in
      // between and rescheduling itself.
      unmountedRef.current = true;
      clearTimeout(retryTimerRef.current);
    };
  }, []);

  // A style swap drops every source, layer and custom image, so re-add them
  // once the new style is in. The ref holds the style the map was built with,
  // so this skips the first run rather than redrawing a map that just loaded.
  const styleIsDarkRef = useRef(isDark);
  useEffect(() => {
    if (map === undefined || styleIsDarkRef.current === isDark) {
      return;
    }
    styleIsDarkRef.current = isDark;
    map.setStyle(mapboxStyleUrl(isDark));
    map.once("style.load", async function () {
      await loadImages(map);
      // The style swap took the old layers with it, so nothing is left to clear.
      drawnLaneIndexRef.current = undefined;
      frameLanesWrapper(map);
      redrawHighlight(map);
    });
  }, [isDark, map]);

  const lanesKey = lanes
    .map(
      (lane) =>
        `${lane.pickup.latitudeLongitude?.latitude},${lane.pickup.latitudeLongitude?.longitude},` +
        `${lane.delivery.latitudeLongitude?.latitude},${lane.delivery.latitudeLongitude?.longitude},` +
        lane.shipmentState,
    )
    .join("|");

  useEffect(() => {
    if (map === undefined) {
      return;
    }

    if (!mapLoaded) {
      map.on("load", function () {
        frameLanesWrapper(map);
        setMapLoaded(true);
      });
    } else {
      frameLanesWrapper(map);
    }
  }, [lanesKey, map]);

  useEffect(() => {
    if (map === undefined || !mapLoaded) {
      return;
    }
    redrawHighlight(map);
    frameLanesWrapper(map);
  }, [highlightedLaneIndex, lanesKey, map, mapLoaded]);

  // The arc is decorative rather than the carrier's real routing, so say so —
  // but only while one is actually on screen.
  const highlightedLane =
    highlightedLaneIndex === undefined
      ? undefined
      : lanes[highlightedLaneIndex];
  const showRouteDisclaimer =
    highlightedLane !== undefined &&
    hasValidLatitudeLongitude(highlightedLane.pickup) &&
    hasValidLatitudeLongitude(highlightedLane.delivery);

  return (
    <MapsFullBleedMapCanvas
      containerRef={mapContainer}
      offsetPx={0}
      showRouteDisclaimer={showRouteDisclaimer}
    />
  );
}
